// Matches routes/api.php (prefix v1, baseURL already includes /api/v1).
export const AUTH_ENDPOINTS = {
  LOGIN: '/login',
  LOGOUT: '/logout',
  LOGOUT_ALL: '/logout-all',
  // Not implemented by the backend yet (no route in routes/api.php) — referenced only behind
  // the VITE_ENABLE_REFRESH_TOKEN flag, which defaults to false until that route exists.
  REFRESH_TOKEN: '/refresh-token',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',
  CHANGE_PASSWORD: '/change-password',
  EMAIL_RESEND: '/email/resend',
  EMAIL_VERIFY: (id: string, hash: string) => `/email/verify/${id}/${hash}`,
  PROFILE: '/profile',
} as const;

// Matches routes/api.php guests.* group (prefix v1, baseURL already includes /api/v1).
export const GUEST_ENDPOINTS = {
  LIST: '/guests',
  CREATE: '/guests',
  DETAIL: (id: number | string) => `/guests/${id}`,
  UPDATE: (id: number | string) => `/guests/${id}`,
  DELETE: (id: number | string) => `/guests/${id}`,
} as const;

// Matches routes/api.php restaurants.* group (prefix v1, baseURL already includes /api/v1).
export const RESTAURANT_ENDPOINTS = {
  LIST: '/restaurants',
  CREATE: '/restaurants',
  DETAIL: (id: number | string) => `/restaurants/${id}`,
  UPDATE: (id: number | string) => `/restaurants/${id}`,
  DELETE: (id: number | string) => `/restaurants/${id}`,
} as const;

// Matches routes/api.php restaurant-hours.* group (prefix v1, baseURL already includes /api/v1).
export const RESTAURANT_HOUR_ENDPOINTS = {
  LIST: '/restaurant-hours',
  CREATE: '/restaurant-hours',
  DETAIL: (id: number | string) => `/restaurant-hours/${id}`,
  UPDATE: (id: number | string) => `/restaurant-hours/${id}`,
  DELETE: (id: number | string) => `/restaurant-hours/${id}`,
} as const;

// Matches routes/api.php restaurant-tables.* group (prefix v1, baseURL already includes /api/v1).
export const RESTAURANT_TABLE_ENDPOINTS = {
  LIST: '/restaurant-tables',
  CREATE: '/restaurant-tables',
  DETAIL: (id: number | string) => `/restaurant-tables/${id}`,
  UPDATE: (id: number | string) => `/restaurant-tables/${id}`,
  DELETE: (id: number | string) => `/restaurant-tables/${id}`,
  UPLOAD_MAP: '/restaurant-tables/upload-map',
  QR_CODE_PDF: (id: number | string) => `/restaurant-tables/${id}/qr-code-pdf`,
} as const;

// Matches routes/api.php reservations.qr.* group (prefix v1, baseURL already includes
// /api/v1). Public, unauthenticated — reached by guests scanning a table's QR code.
export const RESERVATION_QR_ENDPOINTS = {
  TABLE: (restaurantNumber: string, tableId: number | string) =>
    `/reservations/qr/tables/${restaurantNumber}/${tableId}`,
  CREATE_RESERVATION: '/reservations/qr/reservations',
  SEND_OTP: '/reservations/qr/otp',
  VERIFY_OTP: '/reservations/qr/otp/verify',
} as const;

// Matches routes/api.php reservations.* group (prefix v1, baseURL already includes /api/v1).
export const RESERVATION_ENDPOINTS = {
  LIST: '/reservations',
  CREATE: '/reservations',
  DETAIL: (id: number | string) => `/reservations/${id}`,
  UPDATE: (id: number | string) => `/reservations/${id}`,
  CANCEL: (id: number | string) => `/reservations/${id}`,
  AVAILABILITY: '/reservations/availability',
  HOURS: '/reservations/hours',
  CALENDAR: '/reservations/calendar',
  CALENDAR_GRID: '/reservations/calendar-grid',
  DATE_RANGE_AVAILABILITY: '/reservations/date-range-availability',
  WALK_IN: '/reservations/walk-in',
  BULK_CANCEL: '/reservations/bulk-cancel',
  GROUP: '/reservations/group',
  GROUP_MEMBERS: (groupId: number) => `/reservations/group/${groupId}`,
  BOOKING_OCCURRENCES: (bookingId: number) => `/reservations/booking/${bookingId}`,
  SWAP_TABLES: '/reservations/swap-tables',
  MARK_ARRIVED: (id: number | string) => `/reservations/${id}/mark-arrived`,
  UNMARK_ARRIVED: (id: number | string) => `/reservations/${id}/unmark-arrived`,
} as const;

// Matches routes/api.php daily-notes.* group (prefix v1, baseURL already includes /api/v1) —
// the restaurant+date-scoped note text and PDF attachments shown at the bottom of the
// Reservation Listing page for the currently selected restaurant/from-date.
export const DAILY_NOTE_ENDPOINTS = {
  BY_DATE: '/daily-notes/by-date',
  STORE: '/daily-notes',
  UPLOAD_PDF: '/daily-notes/upload-pdf',
  DELETE_PDF: '/daily-notes/delete-pdf',
} as const;

// Matches routes/api.php rates.* group (prefix v1, baseURL already includes /api/v1).
export const RATE_ENDPOINTS = {
  LIST: '/rates',
  CREATE: '/rates',
  DETAIL: (id: number | string) => `/rates/${id}`,
  UPDATE: (id: number | string) => `/rates/${id}`,
  DELETE: (id: number | string) => `/rates/${id}`,
} as const;

// Matches routes/api.php customer-configurations.* group (prefix v1, baseURL already includes /api/v1).
// Read-only — the backend exposes no create/update/delete for configurations.
export const CONFIGURATION_ENDPOINTS = {
  LIST: '/customer-configurations',
} as const;

// Matches routes/api.php users.* group (prefix v1, baseURL already includes /api/v1).
export const USER_ENDPOINTS = {
  LIST: '/users',
  CREATE: '/users',
  DETAIL: (id: number | string) => `/users/${id}`,
  UPDATE: (id: number | string) => `/users/${id}`,
  DELETE: (id: number | string) => `/users/${id}`,
} as const;

// Matches routes/api.php roles.index route (prefix v1, baseURL already includes /api/v1).
export const ROLE_ENDPOINTS = {
  LIST: '/roles',
} as const;

// Matches routes/api.php dashboard.summary route (prefix v1, baseURL already includes /api/v1).
export const DASHBOARD_ENDPOINTS = {
  SUMMARY: '/dashboard/summary',
} as const;

// Matches routes/api.php logs.* group (prefix v1, baseURL already includes /api/v1).
export const LOG_ENDPOINTS = {
  LIST: '/logs',
} as const;

// Matches routes/api.php logs.reports.* group (prefix v1, baseURL already includes /api/v1).
export const REPORT_ENDPOINTS = {
  OCCUPANCY: '/logs/reports/occupancy',
  TOP_GUESTS: '/logs/reports/top-guests',
  CANCELLATIONS: '/logs/reports/cancellations',
} as const;

// Matches routes/api.php customers.* group (prefix v1, baseURL already includes /api/v1).
// "Client" in the UI maps to the backend's Customer (tenant) model — Admin-only.
export const CLIENT_ENDPOINTS = {
  LIST: '/customers',
  CREATE: '/customers',
  DETAIL: (id: number | string) => `/customers/${id}`,
  UPDATE: (id: number | string) => `/customers/${id}`,
  DELETE: (id: number | string) => `/customers/${id}`,
  CONFIGURATIONS: (id: number | string) => `/customers/${id}/configurations`,
  ASSIGN_CONFIGURATION: (id: number | string, configurationId: number | string) =>
    `/customers/${id}/configurations/${configurationId}`,
  CHILD_CATEGORIES: (id: number | string) => `/customers/${id}/child-categories`,
  RESTAURANTS: (id: number | string) => `/customers/${id}/restaurants`,
} as const;

// Matches routes/api.php reservation-configurations.current route (prefix v1, baseURL already
// includes /api/v1) — the current tenant's resolved (override -> legacy column -> default)
// reservation-configuration snapshot, consumed as-is by staff-facing UI.
export const RESERVATION_CONFIGURATION_ENDPOINTS = {
  CURRENT: '/reservation-configurations/current',
} as const;

// Matches routes/api.php child-categories.* group (prefix v1, baseURL already includes
// /api/v1) — staff self-service CRUD, scoped to the authenticated user's own tenant.
export const CHILD_CATEGORY_ENDPOINTS = {
  LIST: '/child-categories',
  CREATE: '/child-categories',
  DETAIL: (id: number | string) => `/child-categories/${id}`,
  UPDATE: (id: number | string) => `/child-categories/${id}`,
  DELETE: (id: number | string) => `/child-categories/${id}`,
} as const;

// Matches routes/api.php reservation-statuses.index route (prefix v1, baseURL already includes
// /api/v1) — the authoritative ReservationStatus enum list (excludes PendingReplacement).
export const RESERVATION_STATUS_ENDPOINTS = {
  LIST: '/reservation-statuses',
} as const;

// Matches routes/api.php templates.* group (prefix v1, baseURL already includes /api/v1).
// Platform-wide (not per-client) — gated by the same "manage-clients" (Admin-only) capability
// as CLIENT_ENDPOINTS above, not a per-tenant operational module.
export const TEMPLATE_ENDPOINTS = {
  LIST: '/templates',
  CREATE: '/templates',
  DETAIL: (id: number | string) => `/templates/${id}`,
  UPDATE: (id: number | string) => `/templates/${id}`,
  DELETE: (id: number | string) => `/templates/${id}`,
  PLACEHOLDERS: '/templates/placeholders',
  PREVIEW: (id: number | string) => `/templates/${id}/preview`,
} as const;

// Matches routes/api.php settings.smtp.* group (prefix v1, baseURL already includes /api/v1).
// Platform-wide singleton — gated by the same Admin-only capability as TEMPLATE_ENDPOINTS.
export const SMTP_SETTING_ENDPOINTS = {
  SHOW: '/settings/smtp',
  UPDATE: '/settings/smtp',
  TEST_CONNECTION: '/settings/smtp/test-connection',
} as const;

// Matches routes/api.php settings.recaptcha.* group (prefix v1, baseURL already includes
// /api/v1). Platform-wide singleton — gated by the same Admin-only capability as TEMPLATE_ENDPOINTS.
export const RECAPTCHA_SETTING_ENDPOINTS = {
  SHOW: '/settings/recaptcha',
  UPDATE: '/settings/recaptcha',
} as const;
